// ═══════════════════════════════════════════════════════════════
// LinkedEntitiesView.tsx – Kapcsolt Entitások & Szervek Kezelése
// Hivatal, Önkormányzat, Intézmények (Óvoda, Iskola, Könyvtár),
// Gazdálkodó szervezetek / Cégek, Közalapítványok
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import type { LinkedEntity, LinkedEntityType, LinkedSettings } from '../../types';
import { DEFAULT_LINKED_SETTINGS, relationLabel } from './linkedModel';
import { LinkedRelationFields, LinkedExtraBlock, RelationTypesEditor, LinkedMapEditor } from './LinkedExtras';
import { useAuth } from '../../context/AuthContext';
import { PhoneInput } from '../common/PhoneInput';
import { usePersistedViewMode } from '../../hooks/usePersistedViewMode';
import {
  Landmark,
  Building2,
  School,
  Building,
  HeartHandshake,
  FolderTree,
  Plus,
  Search,
  ExternalLink,
  Edit2,
  Trash2,
  Mail,
  Phone,
  MapPin,
  User,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  LayoutGrid,
  Table,
  List,
  ChevronDown,
  ChevronRight,
  BookOpen,
  X,
  Globe,
  Newspaper,
  Wallet,
  GitFork,
  Share2,
} from 'lucide-react';

const ENTITY_TYPES: { id: LinkedEntityType | 'all'; label: string; icon: React.FC<{ className?: string }>; color: string; badgeColor: string }[] = [
  { id: 'all', label: 'Összes entitás', icon: FolderTree, color: 'text-slate-600', badgeColor: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  { id: 'hivatal', label: 'Polgármesteri / Közös Hivatal', icon: Landmark, color: 'text-indigo-600', badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300' },
  { id: 'onkormanyzat', label: 'Önkormányzat / Társulás', icon: Building2, color: 'text-blue-600', badgeColor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  { id: 'intezmeny', label: 'Intézmény (Óvoda, Iskola, Könyvtár)', icon: School, color: 'text-emerald-600', badgeColor: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  { id: 'gazdalkodo_szervezet', label: 'Cég / Gazdasági Társaság (Kft.)', icon: Building, color: 'text-amber-600', badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  { id: 'kozalapitvany', label: 'Közalapítvány / Alapítvány', icon: HeartHandshake, color: 'text-purple-600', badgeColor: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
  { id: 'koltsegvetesi_szerv', label: 'Költségvetési szerv', icon: Wallet, color: 'text-cyan-600', badgeColor: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300' },
  { id: 'lap', label: 'Lap / Kiadvány', icon: Newspaper, color: 'text-rose-600', badgeColor: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' },
  { id: 'egyeb', label: 'Egyéb Szerv', icon: FolderTree, color: 'text-slate-600', badgeColor: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
];

export const LinkedEntitiesView: React.FC = () => {
  const { role, user, tenant } = useAuth();
  const canEdit = ['superadmin', 'org_admin', 'tenant_admin', 'editor'].includes(role) ||
                  ['superadmin', 'org_admin', 'tenant_admin', 'editor'].includes(user?.role || '');

  const [entities, setEntities] = useState<LinkedEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState<LinkedEntityType | 'all'>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<LinkedEntity> | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [viewMode, setViewMode] = usePersistedViewMode<'cards' | 'table' | 'accordion'>(
    'linked_entities',
    'cards',
    ['cards', 'table', 'accordion']
  );
  const [expandedAccordions, setExpandedAccordions] = useState<Record<number, boolean>>({});
  // v61: kapcsolati térkép, kiegészítő adatok, szerkeszthető kapcsolattípusok
  const [mainTab, setMainTab] = usePersistedViewMode<'list' | 'map'>('linked_entities_tab', 'list', ['list', 'map']);
  const [allEntities, setAllEntities] = useState<LinkedEntity[]>([]);
  const [linkedSettings, setLinkedSettings] = useState<LinkedSettings>(DEFAULT_LINKED_SETTINGS);
  const [mapReady, setMapReady] = useState(true);
  const [typesEditorOpen, setTypesEditorOpen] = useState(false);
  const loadSettings = async () => {
    const r = await ApiClient.getLinkedSettings();
    if (r.ok && r.data) setLinkedSettings({ ...DEFAULT_LINKED_SETTINGS, ...r.data });
    setMapReady(r.map_ready !== false);
  };
  useEffect(() => { loadSettings(); }, [tenant?.id]);
  /** a kiegészítő blokk nyitva / csukva – szervezeti szinten mentve (szerkesztői jogosultsággal) */
  const toggleExtraOpen = async () => {
    const next = !linkedSettings.extra_open;
    setLinkedSettings((s) => ({ ...s, extra_open: next }));
    if (canEdit) {
      const r = await ApiClient.saveLinkedSettings({ extra_open: next });
      if (r.ok && r.data) setLinkedSettings({ ...DEFAULT_LINKED_SETTINGS, ...r.data });
    }
  };
  const parentName = (id?: number | null) => (id ? allEntities.find((x) => x.id === id)?.name : null);
  /** „kapcsolódik: X · Fenntartó · 100%” sor a listában */
  const relationLine = (item: LinkedEntity) => {
    if (!item.parent_entity_id && !item.relation_type && (item.share_percent === null || item.share_percent === undefined || item.share_percent === '')) return null;
    const parts = [parentName(item.parent_entity_id) || 'anyaszervezet', relationLabel(linkedSettings, item.relation_type), item.share_percent !== null && item.share_percent !== undefined && item.share_percent !== '' ? `${item.share_percent}%` : ''].filter(Boolean);
    return (
      <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-violet-700 dark:text-violet-300">
        <Share2 className="w-3 h-3" /> {parts.join(' · ')}
      </span>
    );
  };
  const openNew = (parentId: number | null = null) => {
    setEditingItem({
      name: '', short_name: '', entity_type: 'intezmeny', pir_id: '', tax_number: '', address: '',
      leader_name: '', leader_title: 'Intézményvezető', phone: '', email: '', website: '',
      hivatali_kapu: '', krid: '', bank_account: '', notes: '', sort_order: 0,
      parent_entity_id: parentId, placement: 'below',
    });
    setModalOpen(true);
  };
  const [selectedEntityForRefs, setSelectedEntityForRefs] = useState<LinkedEntity | null>(null);

  const toggleAccordion = (id: number) => {
    setExpandedAccordions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const loadData = async () => {
    setLoading(true);
    // minden szerv betöltődik (térkép, kapcsolódás), a típusszűrés a felületen történik
    const res = await ApiClient.getLinkedEntities({});
    if (res.ok && res.data) {
      setAllEntities(res.data);
      setEntities(selectedType !== 'all' ? res.data.filter((e) => e.entity_type === selectedType) : res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [selectedType]);

  const filteredEntities = entities.filter((e) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      e.name.toLowerCase().includes(term) ||
      (e.short_name && e.short_name.toLowerCase().includes(term)) ||
      (e.leader_name && e.leader_name.toLowerCase().includes(term)) ||
      (e.pir_id && e.pir_id.includes(term)) ||
      (e.address && e.address.toLowerCase().includes(term))
    );
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem?.name?.trim()) {
      alert('A név megadása kötelező!');
      return;
    }

    setSaving(true);
    let res;
    const { usage_count: _u, references: _r, ...payload } = editingItem as LinkedEntity;
    if (!mapReady) {
      // a v61-es mezők a séma frissítéséig nem menthetők
      for (const k of ['parent_entity_id', 'relation_type', 'placement', 'share_percent', 'bank_account_public', 'postal_address', 'fax', 'customer_service', 'office_hours',
        'activity', 'representative_name', 'founding_doc', 'founding_doc_url', 'board_members', 'founding_act', 'operating_license', 'editorial_office', 'publisher', 'editor_in_chief']) delete (payload as any)[k];
    }
    if (editingItem.id) {
      res = await ApiClient.updateLinkedEntity(editingItem.id, payload);
    } else {
      res = await ApiClient.createLinkedEntity({
        ...payload,
        tenant_id: tenant?.id,
      });
    }

    if (res.ok) {
      setMsg({ type: 'success', text: editingItem.id ? 'Entitás sikeresen módosítva!' : 'Új kapcsolt entitás létrehozva!' });
      setModalOpen(false);
      setEditingItem(null);
      await loadData();
    } else {
      alert('Hiba a mentéskor: ' + (res.error || 'Ismeretlen hiba'));
    }
    setSaving(false);
  };

  const handleDelete = async (item: LinkedEntity) => {
    const count = item.usage_count || 0;
    let confirmMsg = `Biztosan törölni szeretné a(z) "${item.name}" kapcsolt szervezetet?`;
    if (count > 0) {
      confirmMsg = `⚠️ FIGYELEM! KÖZZÉTÉTELI HIVATKOZÁSOK DETEKTÁLVA!\n\n` +
        `A(z) "${item.name}" szervezet jelenleg ${count} db közzétételi pontban / bejegyzésben van hivatalosan hozzárendelve és publikálva!\n\n` +
        `Törlés esetén a bejegyzések nem törlődnek, de a szerv-hozzárendelés automatikusan törlődik belőlük.\n\n` +
        `Biztosan véglegesen törölni kívánja a szervezetet?`;
    }
    if (!confirm(confirmMsg)) return;

    const res = await ApiClient.deleteLinkedEntity(item.id);
    if (res.ok) {
      setMsg({ type: 'success', text: `"${item.name}" sikeresen törölve!` });
      await loadData();
    } else {
      alert('Hiba: ' + (res.error || 'Nem törölhető'));
    }
  };

  const getTypeBadge = (type: LinkedEntityType) => {
    const item = ENTITY_TYPES.find(t => t.id === type) || ENTITY_TYPES[ENTITY_TYPES.length - 1];
    const Icon = item.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold ${item.badgeColor}`}>
        <Icon className="w-3.5 h-3.5" />
        <span>{item.label}</span>
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Fejléc */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/20">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              Kapcsolt Szervek, Intézmények & Cégek
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 text-xs font-semibold border border-emerald-200 dark:border-emerald-900">
                {entities.length} szervezet
              </span>
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Önkormányzat, Hivatal, Óvodák, Iskolák, Könyvtárak, Városüzemeltetési Cégek és Közalapítványok egy helyen
            </p>
          </div>
        </div>

        {canEdit && (
          <button
            onClick={() => openNew(null)}
            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl text-xs font-semibold shadow-md shadow-emerald-500/20 transition flex items-center gap-1.5 cursor-pointer self-start md:self-auto"
          >
            <Plus className="w-4 h-4" />
            <span>Új Kapcsolt Entitás</span>
          </button>
        )}
      </div>

      {msg && (
        <div
          className={`p-4 rounded-xl text-xs font-medium flex items-center justify-between border ${
            msg.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
              : 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {msg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            <span>{msg.text}</span>
          </div>
          <button onClick={() => setMsg(null)} className="text-xs opacity-60 hover:opacity-100 font-bold">✕</button>
        </div>
      )}

      {/* Fülek: lista / kapcsolati térkép */}
      <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-800" role="tablist">
        {([['list', 'Lista', List], ['map', 'Kapcsolati térkép', GitFork]] as const).map(([id, label, Icon]) => (
          <button key={id} role="tab" aria-selected={mainTab === id} onClick={() => setMainTab(id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 cursor-pointer ${mainTab === id ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {mainTab === 'map' && (
        <LinkedMapEditor
          entities={allEntities}
          settings={linkedSettings}
          tenantName={tenant?.name || ''}
          canEdit={canEdit}
          mapReady={mapReady}
          onChanged={loadData}
          onSettings={(st) => setLinkedSettings({ ...DEFAULT_LINKED_SETTINGS, ...st })}
          onAdd={(pid) => openNew(pid)}
          onEdit={(e) => { setEditingItem({ ...e }); setModalOpen(true); }}
        />
      )}

      {mainTab === 'list' && (<>
      {/* Kereső, Típus Szűrők és Nézetváltó */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés intézmény, óvoda, cég, vezető neve, PIR törzsszáma vagy címe szerint..."
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
            />
          </div>

          {/* Nézetmód váltó (Kártyák, Táblázat, Lenyíló lista) */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700 self-end sm:self-auto shrink-0">
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                viewMode === 'cards'
                  ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Kártyás nézet"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Kártyák</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Táblázatos nézet"
            >
              <Table className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Táblázat</span>
            </button>
            <button
              onClick={() => setViewMode('accordion')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                viewMode === 'accordion'
                  ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Lenyíló lista nézet"
            >
              <List className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Lenyíló lista</span>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 pt-1">
          {ENTITY_TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedType(t.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer flex items-center gap-1.5 ${
                  selectedType === t.id
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-slate-50 dark:bg-slate-950/60 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Entitás Lista */}
      {loading ? (
        <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
          <span className="text-xs">Szervezeti entitások betöltése...</span>
        </div>
      ) : filteredEntities.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center">
          <Building2 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">Nincs rögzített kapcsolt entitás</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto mt-1 mb-4">
            Itt vehetők fel a Hivatal, Önkormányzat, intézmények (óvodák, iskolák, művelődési házak), cégek és közalapítványok.
          </p>
          {canEdit && (
            <button
              onClick={() => {
                setEditingItem({
                  name: '',
                  entity_type: 'intezmeny',
                  leader_title: 'Intézményvezető',
                });
                setModalOpen(true);
              }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition"
            >
              Első Intézmény / Cég Rögzítése
            </button>
          )}
        </div>
      ) : viewMode === 'cards' ? (
        /* ── 1. KÁRTYÁS NÉZET ── */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEntities.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs hover:shadow-md transition flex flex-col justify-between group"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  {getTypeBadge(item.entity_type)}
                  {relationLine(item)}
                  {item.pir_id && (
                    <a
                      href={`https://www.allamkincstar.gov.hu/Koltsegvetes/Torzskonyvi_nyilvantartas/ktorzs?pirId=${item.pir_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-950/60 text-[11px] font-mono text-slate-600 dark:text-slate-300 hover:text-blue-600 flex items-center gap-1 transition"
                      title="Megtekintés az Államkincstár törzskönyvi nyilvántartásában"
                    >
                      <span>PIR: {item.pir_id}</span>
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition leading-snug">
                    {item.name}
                  </h3>
                  {item.short_name && item.short_name !== item.name && (
                    <div className="text-xs text-slate-400 mt-0.5">({item.short_name})</div>
                  )}
                </div>

                <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
                  {item.leader_name && (
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="font-semibold text-slate-800 dark:text-slate-200">{item.leader_name}</span>
                      {item.leader_title && <span className="text-[11px] text-slate-400">({item.leader_title})</span>}
                    </div>
                  )}

                  {item.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{item.address}</span>
                    </div>
                  )}

                  {item.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{item.phone}</span>
                    </div>
                  )}

                  {item.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <a href={`mailto:${item.email}`} className="text-indigo-600 dark:text-indigo-400 hover:underline truncate">
                        {item.email}
                      </a>
                    </div>
                  )}

                  {(item.hivatali_kapu || item.krid) && (
                    <div className="pt-1 flex flex-wrap gap-2 text-[11px] font-mono">
                      {item.hivatali_kapu && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          Kapu: {item.hivatali_kapu}
                        </span>
                      )}
                      {item.krid && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          KRID: {item.krid}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Közzétételi hivatkozások száma */}
                  <div className="pt-2">
                    {item.usage_count && item.usage_count > 0 ? (
                      <button
                        onClick={() => setSelectedEntityForRefs(item)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800 border border-blue-200 transition cursor-pointer"
                        title="Kattintson a közzétételi hivatkozások megtekintéséhez"
                      >
                        <BookOpen className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        <span>{item.usage_count} pontban hozzárendelve</span>
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-400 italic">Még nincs közzétételi ponthoz rendelve</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-4 mt-4 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between gap-2">
                {item.website ? (
                  <a
                    href={item.website.startsWith('http') ? item.website : `https://${item.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 font-semibold"
                  >
                    <Globe className="w-3 h-3" />
                    <span>Honlap</span>
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                ) : <div />}

                {canEdit && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => {
                        setEditingItem(item);
                        setModalOpen(true);
                      }}
                      className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-semibold transition cursor-pointer"
                      title="Szerkesztés / Módosítás"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span>Szerkesztés</span>
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="flex items-center gap-1 px-2.5 py-1 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 rounded-lg text-xs font-semibold transition cursor-pointer"
                      title="Törlés"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Törlés</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'table' ? (
        /* ── 2. TÁBLÁZATOS NÉZET ── */
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3.5 font-semibold">Szervezet & Típus</th>
                  <th className="px-4 py-3.5 font-semibold">MÁK PIR / Adószám</th>
                  <th className="px-4 py-3.5 font-semibold">Vezető</th>
                  <th className="px-4 py-3.5 font-semibold">Elérhetőség</th>
                  <th className="px-4 py-3.5 font-semibold">Cím & Kapu</th>
                  <th className="px-4 py-3.5 font-semibold text-center">Közzététel</th>
                  <th className="px-4 py-3.5 text-right font-semibold">Műveletek</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {filteredEntities.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition">
                    <td className="px-4 py-3.5">
                      <div className="font-bold text-slate-900 dark:text-white text-xs">{item.name}</div>
                      {item.short_name && item.short_name !== item.name && (
                        <div className="text-[11px] text-slate-400">({item.short_name})</div>
                      )}
                      <div className="mt-1">{getTypeBadge(item.entity_type)}</div>
                      {relationLine(item)}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-[11px] space-y-1">
                      {item.pir_id ? (
                        <a
                          href={`https://www.allamkincstar.gov.hu/Koltsegvetes/Torzskonyvi_nyilvantartas/ktorzs?pirId=${item.pir_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          <span>PIR: {item.pir_id}</span>
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ) : (
                        <span className="text-slate-400">–</span>
                      )}
                      {item.tax_number && (
                        <div className="text-slate-500">Adószám: {item.tax_number}</div>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {item.leader_name ? (
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-white">{item.leader_name}</div>
                          {item.leader_title && <div className="text-[11px] text-slate-400">{item.leader_title}</div>}
                        </div>
                      ) : (
                        <span className="text-slate-400">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 space-y-1">
                      {item.email && (
                        <div className="flex items-center gap-1 truncate max-w-[180px]">
                          <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                          <a href={`mailto:${item.email}`} className="text-indigo-600 dark:text-indigo-400 hover:underline truncate">
                            {item.email}
                          </a>
                        </div>
                      )}
                      {item.phone && (
                        <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                          <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                          <span>{item.phone}</span>
                        </div>
                      )}
                      {item.website && (
                        <div className="flex items-center gap-1">
                          <Globe className="w-3 h-3 text-slate-400 shrink-0" />
                          <a
                            href={item.website.startsWith('http') ? item.website : `https://${item.website}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-emerald-600 dark:text-emerald-400 hover:underline truncate max-w-[140px]"
                          >
                            Honlap
                          </a>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5 space-y-1">
                      {item.address && (
                        <div className="text-slate-700 dark:text-slate-300 max-w-[180px] truncate" title={item.address}>
                          {item.address}
                        </div>
                      )}
                      {(item.hivatali_kapu || item.krid) && (
                        <div className="font-mono text-[10px] text-slate-500">
                          {item.hivatali_kapu && <span>Kapu: {item.hivatali_kapu} </span>}
                          {item.krid && <span>KRID: {item.krid}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {item.usage_count && item.usage_count > 0 ? (
                        <button
                          onClick={() => setSelectedEntityForRefs(item)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800 border border-blue-200 transition cursor-pointer"
                          title="Hivatkozások megtekintése"
                        >
                          <BookOpen className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                          <span>{item.usage_count} pont</span>
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {canEdit && (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => {
                              setEditingItem(item);
                              setModalOpen(true);
                            }}
                            className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg transition cursor-pointer"
                            title="Szerkesztés / Módosítás"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-1.5 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 rounded-lg transition cursor-pointer"
                            title="Törlés"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ── 3. LENYÍLÓ LISTA (ACCORDION / HARMONIKA) NÉZET ── */
        <div className="space-y-3">
          {filteredEntities.map((item) => {
            const isExpanded = !!expandedAccordions[item.id];
            return (
              <div
                key={item.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs transition"
              >
                {/* Fejléc sáv */}
                <div
                  onClick={() => toggleAccordion(item.id)}
                  className="p-4 sm:px-6 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/70 dark:hover:bg-slate-800/40 select-none"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                      {isExpanded ? <ChevronDown className="w-5 h-5 text-emerald-600" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="text-sm font-bold text-slate-900 dark:text-white truncate">{item.name}</span>
                        {item.short_name && item.short_name !== item.name && (
                          <span className="text-xs text-slate-500 font-medium">({item.short_name})</span>
                        )}
                        {getTypeBadge(item.entity_type)}
                        {relationLine(item)}
                        {item.pir_id && (
                          <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                            PIR: {item.pir_id}
                          </span>
                        )}
                      </div>
                      {item.leader_name && (
                        <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5 truncate">
                          <User className="w-3 h-3 text-slate-400 shrink-0" />
                          <span>{item.leader_name} {item.leader_title ? `(${item.leader_title})` : ''}</span>
                          {item.address && (
                            <>
                              <span className="text-slate-300 dark:text-slate-700">•</span>
                              <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                              <span className="truncate">{item.address}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {item.usage_count && item.usage_count > 0 ? (
                      <button
                        onClick={() => setSelectedEntityForRefs(item)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800 transition cursor-pointer"
                        title="Közzétételi hivatkozások megtekintése"
                      >
                        <BookOpen className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        <span className="hidden sm:inline">{item.usage_count} pontban hozzárendelve</span>
                        <span className="sm:hidden">{item.usage_count} pont</span>
                      </button>
                    ) : (
                      <span className="hidden md:inline text-[11px] text-slate-400">Nincs hivatkozva</span>
                    )}

                    {canEdit && (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => {
                            setEditingItem(item);
                            setModalOpen(true);
                          }}
                          className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-semibold transition cursor-pointer"
                          title="Szerkesztés / Módosítás"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Szerkesztés</span>
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="flex items-center gap-1 px-2.5 py-1 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 rounded-lg text-xs font-semibold transition cursor-pointer"
                          title="Törlés"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Törlés</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Kinyitható Részletes Tartalom */}
                {isExpanded && (
                  <div className="px-6 pb-6 pt-3 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
                      {/* 1. Alapadatok & Cím */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                        <div className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-800 pb-1.5">
                          <Building className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Alapadatok & Székhely</span>
                        </div>
                        {item.address && (
                          <div>
                            <span className="text-slate-400">Cím:</span>
                            <div className="font-medium text-slate-800 dark:text-slate-200">{item.address}</div>
                          </div>
                        )}
                        {item.tax_number && (
                          <div>
                            <span className="text-slate-400">Adószám:</span>
                            <div className="font-mono text-slate-800 dark:text-slate-200">{item.tax_number}</div>
                          </div>
                        )}
                        {item.bank_account && (
                          <div>
                            <span className="text-slate-400">Számlaszám:</span>
                            <div className="font-mono text-slate-800 dark:text-slate-200">{item.bank_account}</div>
                          </div>
                        )}
                        {item.notes && (
                          <div>
                            <span className="text-slate-400">Megjegyzés / Tulajdoni hányad:</span>
                            <div className="text-slate-700 dark:text-slate-300 italic">{item.notes}</div>
                          </div>
                        )}
                      </div>

                      {/* 2. Vezetés & Kapcsolat */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                        <div className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-800 pb-1.5">
                          <User className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Vezetés & Elérhetőségek</span>
                        </div>
                        {item.leader_name && (
                          <div>
                            <span className="text-slate-400">Vezető:</span>
                            <div className="font-semibold text-slate-800 dark:text-slate-200">
                              {item.leader_name} {item.leader_title ? `(${item.leader_title})` : ''}
                            </div>
                          </div>
                        )}
                        {item.phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-slate-400" />
                            <span>{item.phone}</span>
                          </div>
                        )}
                        {item.email && (
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            <a href={`mailto:${item.email}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                              {item.email}
                            </a>
                          </div>
                        )}
                        {item.website && (
                          <div className="flex items-center gap-1.5">
                            <Globe className="w-3.5 h-3.5 text-slate-400" />
                            <a
                              href={item.website.startsWith('http') ? item.website : `https://${item.website}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-600 dark:text-emerald-400 hover:underline"
                            >
                              {item.website}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* 3. Elektronikus Közigazgatási Azonosítók & Hivatkozások */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                        <div className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-800 pb-1.5">
                          <Landmark className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Hivatali Azonosítók & Közzététel</span>
                        </div>
                        {item.pir_id && (
                          <div>
                            <span className="text-slate-400">MÁK PIR azonosító:</span>
                            <div className="font-mono text-slate-800 dark:text-slate-200">{item.pir_id}</div>
                          </div>
                        )}
                        {item.hivatali_kapu && (
                          <div>
                            <span className="text-slate-400">Hivatali Kapu:</span>
                            <div className="font-mono text-slate-800 dark:text-slate-200">{item.hivatali_kapu}</div>
                          </div>
                        )}
                        {item.krid && (
                          <div>
                            <span className="text-slate-400">KRID kód:</span>
                            <div className="font-mono text-slate-800 dark:text-slate-200">{item.krid}</div>
                          </div>
                        )}

                        <div className="pt-1">
                          <span className="text-slate-400">Közzétételi pontok száma:</span>
                          <div className="mt-0.5">
                            {item.usage_count && item.usage_count > 0 ? (
                              <button
                                onClick={() => setSelectedEntityForRefs(item)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800 transition cursor-pointer"
                              >
                                <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                                <span>{item.usage_count} pontban hozzárendelve</span>
                              </button>
                            ) : (
                              <span className="text-slate-500 italic text-[11px]">Nincs közzétételi bejegyzéshez rendelve</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Műveleti sáv az accordion alján is */}
                    {canEdit && (
                      <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                          onClick={() => {
                            setEditingItem(item);
                            setModalOpen(true);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          <span>Szerkesztés & Módosítás</span>
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Entitás Törlése</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      </>)}

      {/* Létrehozó / Szerkesztő Modal */}
      {modalOpen && editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden my-8 animate-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/40">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    {editingItem.id ? 'Kapcsolt Entitás Módosítása' : 'Új Kapcsolt Entitás Rögzítése'}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Önkormányzat, Hivatal, Óvoda, Iskola, Könyvtár, Cég vagy Közalapítvány adatai
                  </p>
                </div>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Entitás Típusa *
                  </label>
                  <select
                    value={editingItem.entity_type || 'intezmeny'}
                    onChange={(e) => setEditingItem({ ...editingItem, entity_type: e.target.value as LinkedEntityType })}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  >
                    {ENTITY_TYPES.filter(t => t.id !== 'all').map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    MÁK PIR Törzskönyvi Azonosító
                  </label>
                  <input
                    type="text"
                    value={editingItem.pir_id || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, pir_id: e.target.value })}
                    placeholder="pl. 123456 (allamkincstar.gov.hu)"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white font-mono"
                  />
                </div>
              </div>

              <LinkedRelationFields
                item={editingItem}
                setItem={(v) => setEditingItem(v)}
                entities={allEntities}
                settings={linkedSettings}
                disabled={!mapReady}
                onEditTypes={canEdit && mapReady ? () => setTypesEditorOpen(true) : undefined}
              />

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Hivatalos Megnevezés *
                </label>
                <input
                  type="text"
                  required
                  value={editingItem.name || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                  placeholder="pl. Napsugár Óvoda és Bölcsőde vagy Városgondnokság Kft."
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Rövid / Megszokott Név
                  </label>
                  <input
                    type="text"
                    value={editingItem.short_name || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, short_name: e.target.value })}
                    placeholder="pl. Napsugár Óvoda"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Adószám (főként cégeknél)
                  </label>
                  <input
                    type="text"
                    value={editingItem.tax_number || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, tax_number: e.target.value })}
                    placeholder="12345678-2-04"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Székhely / Telephely Címe
                </label>
                <input
                  type="text"
                  value={editingItem.address || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, address: e.target.value })}
                  placeholder="pl. 5800 Mezőkovácsháza, Árpád u. 1."
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Vezető Neve
                  </label>
                  <input
                    type="text"
                    value={editingItem.leader_name || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, leader_name: e.target.value })}
                    placeholder="pl. Kiss Ilona"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Vezető Beosztása
                  </label>
                  <input
                    type="text"
                    value={editingItem.leader_title || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, leader_title: e.target.value })}
                    placeholder="pl. Intézményvezető, Igazgató, Ügyvezető"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="le-phone" className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Telefonszám
                  </label>
                  <PhoneInput
                    key={editingItem.id ?? 'new'}
                    id="le-phone"
                    compact
                    value={editingItem.phone}
                    onChange={(v) => setEditingItem({ ...editingItem, phone: v })}
                    placeholder="+36 68 123 456"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    E-mail Cím
                  </label>
                  <input
                    type="email"
                    value={editingItem.email || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, email: e.target.value })}
                    placeholder="intezmeny@telepules.hu"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Honlap Cím
                  </label>
                  <input
                    type="text"
                    value={editingItem.website || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, website: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Hivatali Kapu Rövid Név
                  </label>
                  <input
                    type="text"
                    value={editingItem.hivatali_kapu || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, hivatali_kapu: e.target.value })}
                    placeholder="pl. MKHOVI"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    KRID Azonosító
                  </label>
                  <input
                    type="text"
                    value={editingItem.krid || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, krid: e.target.value })}
                    placeholder="pl. 111199999"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Számlaszám
                  </label>
                  <input
                    type="text"
                    value={editingItem.bank_account || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, bank_account: e.target.value })}
                    placeholder="117...-..."
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white font-mono"
                  />
                </div>
              </div>

              <label className={`flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 ${!mapReady ? 'opacity-50' : ''}`}>
                <input type="checkbox" disabled={!mapReady} checked={!!editingItem.bank_account_public}
                  onChange={(e) => setEditingItem({ ...editingItem, bank_account_public: e.target.checked ? 1 : 0 })} />
                A számlaszám a nyilvános felületen is megjelenhet
              </label>

              <LinkedExtraBlock
                item={editingItem}
                setItem={(v) => setEditingItem(v)}
                open={linkedSettings.extra_open}
                onToggle={toggleExtraOpen}
                disabled={!mapReady}
              />

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Megjegyzés (belső, nem jelenik meg a nyilvános felületen)
                </label>
                <textarea
                  rows={2}
                  value={editingItem.notes || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })}
                  placeholder="pl. 100%-os önkormányzati tulajdonú gazdasági társaság..."
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shadow-md shadow-emerald-500/20 transition disabled:opacity-50"
                >
                  {saving ? 'Mentés...' : 'Entitás Mentése'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Közzétételi Hivatkozások Részletes Modal */}
      {selectedEntityForRefs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden my-8 animate-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/40">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    Közzétételi Hivatkozások
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-semibold text-slate-700 dark:text-slate-200">{selectedEntityForRefs.name}</span>
                    {' '}— {selectedEntityForRefs.usage_count || 0} db közzétételi pontban hozzárendelve
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedEntityForRefs(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Ez a szerv / intézmény az alábbi közzétételi pontok bejegyzéseihez van társítva. Amennyiben törli az entitást, a bejegyzések nem törlődnek, de a szerv-hozzárendelés automatikusan lekerül róluk:
              </p>

              {selectedEntityForRefs.references && selectedEntityForRefs.references.length > 0 ? (
                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden divide-y divide-slate-100 dark:divide-slate-800/60">
                  {selectedEntityForRefs.references.map((ref, idx) => (
                    <div
                      key={idx}
                      className="p-3.5 bg-white dark:bg-slate-900/60 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition flex items-start justify-between gap-3 text-xs"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 font-mono font-bold text-[11px] border border-blue-200 dark:border-blue-900/50 shrink-0">
                            Pont: {ref.pont}
                          </span>
                          <span className="font-semibold text-slate-900 dark:text-white truncate">
                            {ref.megnevezes}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                          <span>Oldal / Melléklet:</span>
                          <span className="font-medium text-slate-700 dark:text-slate-300">{ref.page_title}</span>
                          <span className="text-slate-300 dark:text-slate-700">•</span>
                          <span className="font-mono text-slate-400">Rekord ID: #{ref.record_id}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-slate-400 bg-slate-50/50 dark:bg-slate-950/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                  <div className="text-xs font-medium text-slate-600 dark:text-slate-300">
                    Nincs közvetlen közzétételi pont hozzárendelve ehhez a szervhez.
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    A szerv biztonságosan törölhető a rendszerből közzétételi ütközés nélkül.
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-slate-50/60 dark:bg-slate-950/40 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    const ent = selectedEntityForRefs;
                    setSelectedEntityForRefs(null);
                    setEditingItem(ent);
                    setModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  <span>Szervezet Szerkesztése</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedEntityForRefs(null)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shadow-xs transition ml-auto"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}
      {typesEditorOpen && (
        <RelationTypesEditor
          settings={linkedSettings}
          entities={allEntities}
          onClose={() => setTypesEditorOpen(false)}
          onSaved={(st) => { setLinkedSettings({ ...DEFAULT_LINKED_SETTINGS, ...st }); setTypesEditorOpen(false); }}
        />
      )}
    </div>
  );
};
